import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FilterPanel from './components/FilterPanel';
import QuestionCard from './components/QuestionCard';
import PracticeControls from './components/PracticeControls';
import SessionSetupModal from './components/SessionSetupModal';

const QuestionPractice = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSessionSetupOpen, setIsSessionSetupOpen] = useState(false);
  const [sessionActive, setSessionActive] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [sessionConfig, setSessionConfig] = useState(null);
  const [bookmarkedQuestions, setBookmarkedQuestions] = useState([]);
  const [answeredQuestions, setAnsweredQuestions] = useState({});

  const [filters, setFilters] = useState({
    knowledgeAreas: [],
    difficulty: [],
    questionTypes: [],
    answerStatus: []
  });

  const mockQuestions = [
    {
      id: 1,
      number: 1,
      knowledgeArea: "Business Analysis Planning",
      difficulty: "intermediate",
      type: "scenario-based",
      scenario: "A business analyst is working with a large financial institution to implement a new customer relationship management (CRM) system. The stakeholders include the IT department, sales team, customer service representatives, and senior management. Each group has different priorities and concerns about the new system.\n\nThe IT department is concerned about system integration with existing infrastructure. The sales team wants mobile access and real-time data synchronization. Customer service representatives need comprehensive customer history views. Senior management requires detailed analytics and reporting capabilities.\n\nWhat should be the business analyst's FIRST step in this situation?",
      options: [
        { id: "a", text: "Begin developing detailed technical specifications for the IT department to review and approve before proceeding with other stakeholder requirements" },
        { id: "b", text: "Conduct a stakeholder analysis to identify all stakeholders, their interests, influence levels, and communication preferences to develop an appropriate engagement strategy" },
        { id: "c", text: "Schedule individual meetings with each stakeholder group to gather their specific requirements and document them in separate requirement documents" },
        { id: "d", text: "Create a project charter that outlines the scope, objectives, and deliverables, then distribute it to all stakeholders for their approval and sign-off" }
      ],
      correctAnswer: "b",
      explanation: "According to BABOK Guide, stakeholder analysis is a foundational activity in Business Analysis Planning and Monitoring. Before gathering requirements or creating documentation, the business analyst must understand who the stakeholders are, their level of influence, their interests, and how best to engage with them.\n\nThis approach ensures that:\n• All relevant stakeholders are identified and properly engaged\n• Communication strategies are tailored to each stakeholder group\n• The BA can prioritize stakeholder needs based on their influence and interest\n• Potential conflicts between stakeholder groups can be anticipated and managed\n\nThe other options represent activities that should occur AFTER stakeholder analysis is complete.",
      reference: "BABOK Guide v3, Section 3.1 - Plan Stakeholder Engagement"
    },
    {
      id: 2,
      number: 2,
      knowledgeArea: "Elicitation and Collaboration",
      difficulty: "advanced",
      type: "scenario-based",
      scenario: "During a requirements elicitation workshop for a healthcare management system, a business analyst notices that one senior physician is dominating the conversation while nurses and administrative staff remain silent. The physician's requirements focus heavily on clinical features, but the BA knows that workflow efficiency for all staff is equally important.\n\nThe workshop has 90 minutes remaining, and the BA needs to ensure balanced input from all participants. Several nurses have attempted to speak but were interrupted. The administrative staff appears disengaged and is checking their phones.\n\nWhat is the MOST effective approach for the business analyst to handle this situation?",
      options: [
        { id: "a", text: "Continue with the current format but schedule separate follow-up sessions with nurses and administrative staff to gather their requirements privately" },
        { id: "b", text: "Politely interrupt the physician and implement a round-robin technique where each participant gets equal time to share their requirements without interruption" },
        { id: "c", text: "Take a 10-minute break, then restructure the session using breakout groups organized by role, followed by group presentations of consolidated requirements" },
        { id: "d", text: "Thank the physician for their input, then directly ask specific nurses and administrative staff members targeted questions about their workflow needs" }
      ],
      correctAnswer: "c",
      explanation: "This approach addresses multiple issues simultaneously and aligns with BABOK best practices for facilitation:\n\n1. The break allows the BA to reset the session dynamics and gives disengaged participants a mental refresh\n2. Breakout groups by role create a safe space for less vocal participants to contribute without intimidation\n3. Group presentations ensure all perspectives are heard and documented\n4. This technique prevents any single stakeholder from dominating while maintaining professional relationships\n\nOption B might create confrontation and damage stakeholder relationships. Option A delays critical input and may result in conflicting requirements. Option D puts individuals on the spot and may not address the underlying group dynamics issue.\n\nEffective facilitation requires the BA to recognize and adapt to group dynamics in real-time.",
      reference: "BABOK Guide v3, Section 4.1 - Prepare for Elicitation, Section 4.5 - Manage Stakeholder Collaboration"
    },
    {
      id: 3,
      number: 3,
      knowledgeArea: "Requirements Analysis",
      difficulty: "beginner",
      type: "definition",
      scenario: "A business analyst is reviewing a set of requirements for a new e-commerce platform. One requirement states: 'The system shall process orders quickly and efficiently.'\n\nWhat is the PRIMARY issue with this requirement?",
      options: [
        { id: "a", text: "It is not testable because \'quickly\' and \'efficiently\' are subjective terms without measurable criteria" },
        { id: "b", text: "It is too technical and should be written in business language that stakeholders can understand" },
        { id: "c", text: "It combines two separate requirements and should be split into distinct requirement statements" },
        { id: "d", text: "It is a solution requirement rather than a business requirement and should be removed from the document" }
      ],
      correctAnswer: "a",
      explanation: "This requirement violates the fundamental principle that requirements must be testable and verifiable. The terms 'quickly' and 'efficiently' are subjective and cannot be objectively measured or tested.\n\nA properly written requirement would specify measurable criteria, such as:\n• 'The system shall process 95% of orders within 3 seconds'\n• 'The system shall handle 1000 concurrent orders without performance degradation'\n\nTestability is one of the key characteristics of high-quality requirements according to BABOK. Without measurable criteria, it's impossible to:\n• Verify that the requirement has been met\n• Test the system objectively\n• Determine project completion\n• Manage stakeholder expectations\n\nWhile the requirement could potentially be improved in other ways, the lack of testability is the PRIMARY issue that must be addressed.",
      reference: "BABOK Guide v3, Section 5.3 - Verify Requirements"
    }
  ];

  const [sessionQuestions, setSessionQuestions] = useState([]);

  useEffect(() => {
    const mode = searchParams?.get('mode');
    const questions = searchParams?.get('questions');
    
    if (mode === 'full' && questions === '150') {
      setIsSessionSetupOpen(true);
    } else if (mode === 'quick' && questions === '20') {
      startQuickSession();
    }
  }, [searchParams]);

  useEffect(() => {
    if (sessionActive && sessionConfig?.timedMode && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleEndSession();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [sessionActive, sessionConfig, timeRemaining]);

  const startQuickSession = () => {
    const config = {
      questionCount: 20,
      timedMode: false,
      showExplanations: true,
      randomOrder: true
    };
    startSession(config);
  };

  const startSession = (config) => {
    setSessionConfig(config);
    const questions = [...mockQuestions]?.slice(0, Math.min(config?.questionCount, mockQuestions?.length));
    
    if (config?.randomOrder) {
      questions?.sort(() => Math.random() - 0.5);
    }
    
    setSessionQuestions(questions);
    setSessionActive(true);
    setCurrentQuestionIndex(0);
    
    if (config?.timedMode) {
      setTimeRemaining(config?.timeLimit * 60);
    }
  };

  const handleFilterChange = (category, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleApplyFilters = () => {
    setIsSessionSetupOpen(true);
    setIsFilterOpen(false);
  };

  const handleResetFilters = () => {
    setFilters({
      knowledgeAreas: [],
      difficulty: [],
      questionTypes: [],
      answerStatus: []
    });
  };

  const handleAnswerSubmit = (answerId) => {
    const currentQuestion = sessionQuestions?.[currentQuestionIndex];
    setAnsweredQuestions(prev => ({
      ...prev,
      [currentQuestion?.id]: {
        selectedAnswer: answerId,
        isCorrect: answerId === currentQuestion?.correctAnswer,
        timestamp: new Date()?.toISOString()
      }
    }));
  };

  const handleBookmark = () => {
    const currentQuestion = sessionQuestions?.[currentQuestionIndex];
    setBookmarkedQuestions(prev => {
      if (prev?.includes(currentQuestion?.id)) {
        return prev?.filter(id => id !== currentQuestion?.id);
      }
      return [...prev, currentQuestion?.id];
    });
  };

  const handleNext = () => {
    if (currentQuestionIndex < sessionQuestions?.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      handleEndSession();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleEndSession = () => {
    const results = {
      totalQuestions: sessionQuestions?.length,
      answeredQuestions: Object.keys(answeredQuestions)?.length,
      correctAnswers: Object.values(answeredQuestions)?.filter(a => a?.isCorrect)?.length,
      timeSpent: sessionConfig?.timedMode ? (sessionConfig?.timeLimit * 60 - timeRemaining) : null,
      bookmarkedCount: bookmarkedQuestions?.length,
      sessionConfig,
      answers: answeredQuestions
    };

    navigate('/results-analytics', { state: { results } });
  };

  const currentQuestion = sessionQuestions?.[currentQuestionIndex];
  const currentAnswer = currentQuestion ? answeredQuestions?.[currentQuestion?.id] : null;
  const isCurrentBookmarked = currentQuestion ? bookmarkedQuestions?.includes(currentQuestion?.id) : false;

  const getAvailableQuestionCount = () => {
    return 120000;
  };

  if (!sessionActive) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background pt-[60px]">
          <div className="flex h-[calc(100vh-60px)]">
            <div className="w-80 flex-shrink-0 hidden lg:block">
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onApplyFilters={handleApplyFilters}
                onResetFilters={handleResetFilters}
                questionCount={getAvailableQuestionCount()}
                isOpen={true}
                onClose={() => {}}
              />
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="max-w-4xl mx-auto p-4 md:p-6 lg:p-8">
                <div className="mb-6 lg:hidden">
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={() => setIsFilterOpen(true)}
                    iconName="Filter"
                    iconPosition="left"
                  >
                    Show Filters
                  </Button>
                </div>

                <div className="bg-card rounded-xl shadow-elevation-2 p-6 md:p-8 mb-6">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="BookOpen" size={24} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                        Question Practice
                      </h1>
                      <p className="text-sm md:text-base text-muted-foreground">
                        Access over 120,000 PSI-IIBA template scenario-based questions for comprehensive CBAP exam preparation
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-primary/10 rounded-lg p-4 border border-primary/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name="Database" size={20} className="text-primary" />
                        <span className="text-sm font-medium text-primary">Total Questions</span>
                      </div>
                      <p className="text-2xl md:text-3xl font-bold text-primary data-text">120,000+</p>
                    </div>

                    <div className="bg-secondary/10 rounded-lg p-4 border border-secondary/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name="Target" size={20} className="text-secondary" />
                        <span className="text-sm font-medium text-secondary">Knowledge Areas</span>
                      </div>
                      <p className="text-2xl md:text-3xl font-bold text-secondary data-text">6</p>
                    </div>

                    <div className="bg-success/10 rounded-lg p-4 border border-success/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name="Award" size={20} className="text-success" />
                        <span className="text-sm font-medium text-success">PSI-IIBA Format</span>
                      </div>
                      <p className="text-2xl md:text-3xl font-bold text-success data-text">100%</p>
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-xl shadow-elevation-2 p-6 md:p-8">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Getting Started</h2>
                  <div className="space-y-4 text-sm md:text-base text-muted-foreground">
                    <div className="flex items-start gap-3">
                      <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
                      <p>Use the filter panel to select specific knowledge areas, difficulty levels, or question types</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
                      <p>Choose between timed practice mode for exam simulation or untimed mode for learning</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
                      <p>Bookmark challenging questions for later review and track your progress over time</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
                      <p>Review detailed explanations and BABOK references after each question</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isFilterOpen && (
            <div className="fixed inset-0 z-50 lg:hidden bg-background">
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onApplyFilters={handleApplyFilters}
                onResetFilters={handleResetFilters}
                questionCount={getAvailableQuestionCount()}
                isOpen={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}
              />
            </div>
          )}

          <SessionSetupModal
            isOpen={isSessionSetupOpen}
            onClose={() => setIsSessionSetupOpen(false)}
            onStartSession={startSession}
            availableQuestions={getAvailableQuestionCount()}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background pt-[60px] pb-[80px]">
        <div className="max-w-4xl mx-auto p-4 md:p-6 lg:p-8">
          <QuestionCard
            question={currentQuestion}
            onAnswerSubmit={handleAnswerSubmit}
            onBookmark={handleBookmark}
            isBookmarked={isCurrentBookmarked}
            showExplanation={sessionConfig?.showExplanations && currentAnswer}
            selectedAnswer={currentAnswer?.selectedAnswer}
            isSubmitted={!!currentAnswer}
          />
        </div>

        <PracticeControls
          currentQuestion={currentQuestionIndex + 1}
          totalQuestions={sessionQuestions?.length}
          timeRemaining={timeRemaining}
          isTimedMode={sessionConfig?.timedMode}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onEndSession={handleEndSession}
          canGoNext={!!currentAnswer || currentQuestionIndex < sessionQuestions?.length - 1}
          canGoPrevious={currentQuestionIndex > 0}
        />
      </div>
    </>
  );
};

export default QuestionPractice;